import { DeleteOutlined, SettingOutlined, TeamOutlined } from "@ant-design/icons";
import { Button, Card, Col, Modal, Popconfirm, Row, Typography, Skeleton, Result } from "antd";
import axios from "axios";
import { message } from "components/Messages/Messages";
import { Event, EventRegistration, IsOpenType, NoShow } from "interfaces/events";
import { StudyProgram } from "interfaces/students";
import { StudentUser } from "interfaces/users";
import { useCallback, useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import AdminSingleEventHeader from "./AdminEventHeader";
import EventStats from "./Statistics/EventStats";
import ClassYearDistribution from "./Statistics/ClassyearDistribution";
import StudyProgramDistribution from "./Statistics/StudyProgramDistribution";
import QRCodeEvent from "./QRcode";
import DeregisteredStudents from "./RegistrationLists/DeregisteredStudents";
import RegisteredStudents from "./RegistrationLists/RegisteredStudents";
import Waitlist from "./RegistrationLists/Waitlist";
import { NoShows } from "./NoShows";

const { Text, Paragraph } = Typography;

export interface RegisteredStudentData {
  key: number;
  id: number;
  name: string;
  username: string;
  study_programme_id: number;
  study_programme_short_name: string;
  study_programme_name: string;
  year_level: number;
  mobile_number: string;
  email: string;
  allergies: string;
  time_attended?: string;
  created_at: string;
}

interface StudentsData {
  registered: RegisteredStudentData[];
  waitlist: RegisteredStudentData[];
  deregistered: RegisteredStudentData[];
}

const AdminSingleEventPage = () => {
  const [event, setEvent] = useState<Event>();
  const { id } = useParams<{ id: string }>();
  const [loadingStudents, setLoadingStudents] = useState<boolean>(true);
  const [noShows, setNoShows] = useState<NoShow[]>([]);
  const [fetchingEvent, setFetchingEvent] = useState<boolean>(true);

  const [studentsData, setStudentsData] = useState<StudentsData>({
    registered: [],
    waitlist: [],
    deregistered: [],
  });

  const browserHistory = useHistory();

  useEffect(() => {
    axios
      .get<Event>(`/events/admin-events/${id}/`)
      .then(response => {
        setEvent(response.data);
      })
      .finally(() => {
        setFetchingEvent(false);
      });
  }, [id]);

  useEffect(() => {
    axios.get<NoShow[]>(`/events/${id}/no-shows/`).then(response => {
      setNoShows(response.data);
    });
  }, [event?.no_shows_sent, event, id]);

  const fetchRegistrationsData = useCallback(async () => {
    if (!event) return;
    setLoadingStudents(true);
    try {
      const [registrations, waitlist, deregistrations] = await Promise.all([
        axios.get<EventRegistration[]>(`/events/${event.id}/registrations/`),
        axios.get<EventRegistration[]>(`/events/${event.id}/waitlist/`),
        axios.get<EventRegistration[]>(`/events/${event.id}/deregistrations/`),
      ]);

      const allRegistrations = [
        ...registrations.data.map(r => ({ ...r, type: "registered" })),
        ...waitlist.data.map(r => ({ ...r, type: "waitlist" })),
        ...deregistrations.data.map(r => ({ ...r, type: "deregistered" })),
      ];

      const studentPromises = allRegistrations.map(reg => axios.get<StudentUser>(`/students/${reg.student}/`));
      const studentResponses = await Promise.all(studentPromises);

      const studyProgrammeCache = new Map<number, StudyProgram>();
      const studyProgrammePromises = studentResponses.map(async res => {
        const studyProgrammeId = res.data.study_programme;
        if (!studyProgrammeCache.has(studyProgrammeId)) {
          const studyProgrammeData = await axios.get<StudyProgram>(`/students/study-program/${studyProgrammeId}/`);
          studyProgrammeCache.set(studyProgrammeId, studyProgrammeData.data);
        }
        return studyProgrammeCache.get(studyProgrammeId);
      });

      const studyProgrammeResults = await Promise.all(studyProgrammePromises);

      const combinedData = allRegistrations.map((reg, index) => {
        const student: StudentUser = studentResponses[index].data;
        const studyProgramme: StudyProgram | undefined = studyProgrammeResults[index];

        return {
          key: index + 1,
          number: index + 1,
          id: student.id,
          name: `${student.first_name} ${student.last_name}`,
          username: student.username,
          study_programme_id: student.study_programme,
          study_programme_short_name: studyProgramme?.shortname || "",
          study_programme_name: studyProgramme?.name || "",
          year_level: student.year_level,
          mobile_number: student.mobile_number,
          email: student.email,
          allergies: student.allergies,
          time_attended: reg?.time_attended,
          created_at: new Date(reg.created_at).toLocaleString("no-NO", {
            day: "2-digit",
            month: "2-digit",
            year: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          }),
          type: reg.type,
        };
      });

      setStudentsData({
        registered: combinedData.filter(d => d.type === "registered"),
        waitlist: combinedData.filter(d => d.type === "waitlist"),
        deregistered: combinedData.filter(d => d.type === "deregistered"),
      });

      setLoadingStudents(false);
    } catch {
      setLoadingStudents(false);
    }
  }, [event]);

  const handleCloseEvent = () => {
    axios
      .put<Event>(`/events/admin-events/${id}/`, { ...event, is_open: IsOpenType.CLOSED })
      .then(response => {
        message("success", "Arrangementet er stengt for påmelding og avmelding");
        setEvent(response.data);
      })
      .catch(() => {
        message("error", "Kunne ikke stenge arrangementet");
      });
  };

  const handleOpenEvent = () => {
    axios
      .put<Event>(`/events/admin-events/${id}/`, { ...event, is_open: IsOpenType.OPENED })
      .then(response => {
        message("success", "Arrangementet er åpnet.");
        setEvent(response.data);
      })
      .catch(() => {
        message("error", "Kunne ikke åpne arrangementet");
      });
  };

  const handleInnslipp = () => {
    browserHistory.push(`/admin/tools/take-attendance-for-event/${id}`);
  };

  const handleDeleteEvent = () => {
    axios
      .delete<Event>(`/events/admin-events/${id}/`)
      .then(() => {
        message("success", "Arrangementet er slettet.");
        browserHistory.push("/admin/events/");
      })
      .catch(() => {
        message("error", "Kunne ikke slette arrangementet.");
      });
  };

  const handleSendNoShow = () => {
    if (!event || event.is_open !== IsOpenType.CLOSED) {
      message("warning", "Steng arrangementet først for å sende No Show.");
      return;
    }

    Modal.confirm({
      title: "Send No Show",
      content: (
        <>
          {event.is_open !== IsOpenType.CLOSED ? (
            <Text>Steng arrangementet først for å sende No Show.</Text>
          ) : (
            <>
              <Paragraph>
                Er du sikker på at du vil sende No Show til alle påmeldte studenter som ikke har registrert oppmøte?
              </Paragraph>
              <Paragraph>
                Følgende studenter vil få en prikk: <br />
                <ul>
                  {studentsData.registered
                    .filter(student => !student.time_attended)
                    .map(student => (
                      <li key={student.username}>{student.name}</li>
                    ))}
                  {studentsData.registered.filter(student => !student.time_attended).length === 0 && (
                    <li>Ingen studenter</li>
                  )}
                </ul>
                <Text italic>
                  Hvis noen i listen har registrert oppmøte etter du åpnet denne siden vil de ikke få prikk.
                </Text>
              </Paragraph>
            </>
          )}
        </>
      ),
      okText: "Send No Show",
      okType: "danger",
      cancelText: "Avbryt",
      onOk: () => {
        axios
          .post(`/events/${id}/send-no-show/`)
          .then(() => {
            message("success", "No Show er sendt");
            setEvent({ ...event, no_shows_sent: true });
            fetchRegistrationsData();
          })
          .catch(() => {
            message("error", "Feil under sending av No Show");
          });
      },
    });
  };

  useEffect(() => {
    fetchRegistrationsData();
  }, [fetchRegistrationsData]);

  if (fetchingEvent)
    return (
      <>
        <Row>
          <Skeleton.Button block active style={{ width: "100%", height: "150px" }} />
        </Row>
        <Row>
          <Skeleton.Button block active style={{ height: "75px", margin: "16px 0" }} />
        </Row>
        <Row gutter={[16, 16]}>
          <Col lg={4} md={8} sm={12} xs={24}>
            <Skeleton.Button block active style={{ height: "150px" }} />
          </Col>
          <Col lg={4} md={8} sm={12} xs={24}>
            <Skeleton.Button block active style={{ height: "150px" }} />
          </Col>
          <Col lg={4} md={8} sm={12} xs={24}>
            <Skeleton.Button block active style={{ height: "150px" }} />
          </Col>
          <Col lg={4} md={8} sm={12} xs={24}>
            <Skeleton.Button block active style={{ height: "150px" }} />
          </Col>
          <Col lg={4} md={8} sm={12} xs={24}>
            <Skeleton.Button block active style={{ height: "150px" }} />
          </Col>
          <Col lg={4} md={8} sm={12} xs={24}>
            <Skeleton.Button block active style={{ height: "150px" }} />
          </Col>
        </Row>
        <Row style={{ marginTop: "16px" }}>
          <Skeleton.Button block active style={{ height: "300px" }} />
        </Row>
        <Row style={{ marginTop: "16px" }}>
          <Skeleton.Button block active style={{ height: "300px" }} />
        </Row>
        <Row style={{ marginTop: "16px" }}>
          <Skeleton.Button block active style={{ height: "300px" }} />
        </Row>
      </>
    );

  if (!event)
    return <Result title="Fant ikke arrangementet" subTitle="Dobbeltsjekk URL eller prøv på nytt" status="404" />;

  return (
    <>
      <AdminSingleEventHeader event={event} />
      <Card style={{ width: "100%", marginTop: "16px" }}>
        <Row gutter={[16, 16]}>
          <Col lg={4} sm={24} style={{ display: "flex" }}>
            {event.is_open === IsOpenType.OPENED || event.is_open === IsOpenType.DEREGISTERING_CLOSED ? (
              <Popconfirm
                key="close-event"
                title="Sikker på at du vil stenge for påmelding og avmelding?"
                okText="Steng"
                cancelText="Avbryt"
                okType="danger"
                onConfirm={handleCloseEvent}
              >
                <Button type="primary" danger>
                  Steng
                </Button>
              </Popconfirm>
            ) : event.is_open === IsOpenType.NOT_OPENED ? (
              <Popconfirm
                key="open-event"
                title="Sikker på at du vil åpne arrangementet?"
                okText="Åpne"
                cancelText="Avbryt"
                onConfirm={handleOpenEvent}
              >
                <Button type="primary">Åpne påmelding</Button>
              </Popconfirm>
            ) : !event.no_shows_sent ? (
              <Button type="primary" onClick={handleInnslipp}>
                Innslipp
              </Button>
            ) : (
              <Button disabled>Fullført - Arrangementet er stengt og No Show er sendt</Button>
            )}
            {(new Date(event.start) < new Date() || event.is_open === IsOpenType.CLOSED) && !event.no_shows_sent && (
              <Button danger onClick={handleSendNoShow} style={{ marginLeft: "5px" }}>
                Send No Show
              </Button>
            )}
          </Col>
          <Col lg={20} sm={24} style={{ textAlign: "right" }}>
            <Link to={`/admin/events/${id}/edit`}>
              <Button type="primary" title="Rediger arrangementet">
                <SettingOutlined />
              </Button>
            </Link>
            <Link to={`/admin/events/${id}/seats`}>
              <Button type="primary" title="Rediger plassfordeling" style={{ margin: "0 5px" }}>
                <TeamOutlined />
              </Button>
            </Link>
            <QRCodeEvent eventId={event.id} />
            <Popconfirm
              key="delete-event"
              title="Sikker på at du vil slette arrangementet?"
              okText="Slett"
              cancelText="Avbryt"
              okType="danger"
              onConfirm={handleDeleteEvent}
            >
              <Button type="primary" title="Slett arrangementet" danger>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      </Card>
      {(event.no_shows_sent || noShows.length > 0) && (
        <Row>
          <NoShows noShows={noShows} />
        </Row>
      )}
      <Row style={{ marginBottom: "16px" }}>
        <EventStats
          seats={event.seats}
          waitingListSize={event?.use_waiting_list ? event?.waiting_list_size : 0}
          waitlist={studentsData.waitlist}
          registeredStudents={studentsData.registered}
          noShows={noShows.filter(n => n.no_show_type === 0).length}
        />
      </Row>
      {studentsData.registered.length > 0 && (
        <Row
          gutter={[16, 16]}
          style={{ width: "calc(100% + 16px)", display: "flex", alignItems: "stretch", marginBottom: "16px" }}
        >
          <Col md={12} sm={24}>
            <ClassYearDistribution registeredStudents={studentsData.registered} />
          </Col>
          <Col md={12} sm={24}>
            <StudyProgramDistribution registeredStudents={studentsData.registered} />
          </Col>
        </Row>
      )}
      <Row>
        <Waitlist
          loading={loadingStudents}
          registeredStudentsData={studentsData.waitlist}
          onStudentRegistered={fetchRegistrationsData}
          eventId={event.id}
        />
      </Row>
      <Row>
        <RegisteredStudents
          loading={loadingStudents}
          eventId={event.id}
          registeredStudentsData={studentsData.registered}
          onStudentDeregistered={(studentId: number) => {
            setStudentsData(prevData => ({
              ...prevData,
              registered: prevData.registered.filter(s => s.id !== studentId),
            }));
            fetchRegistrationsData(); // Fetch to see if someone is registered from waitlist etc.
          }}
          onStudentRegistered={fetchRegistrationsData}
        />
      </Row>
      <Row>
        <DeregisteredStudents loading={loadingStudents} registeredStudentsData={studentsData.deregistered} />
      </Row>
    </>
  );
};

export default AdminSingleEventPage;
